import React, { useMemo, useState } from 'react';
import './SkinMacroAnalysisReport.css';

const REGION_ORDER = ['forehead', 'cheek', 'left_cheek', 'right_cheek'];

const SCORE_META = [
  { key: 'macro_condition_score', label: 'Macro Condition', hint: 'Higher = better overall visual condition' },
  { key: 'surface_smoothness', label: 'Surface Smoothness', hint: 'Higher = smoother-looking surface' },
  { key: 'hydration_appearance', label: 'Hydration Appearance', hint: 'Higher = more hydrated-looking' },
  { key: 'oiliness_appearance', label: 'Oiliness Appearance', hint: 'Higher = more oily/shiny-looking' },
  { key: 'texture_regularity', label: 'Texture Regularity', hint: 'Higher = more even' },
  { key: 'pore_follicle_clarity', label: 'Pore / Follicle Clarity', hint: 'Higher = clearer-looking openings' },
  { key: 'redness_level', label: 'Redness Level', hint: 'Higher = more redness' },
  { key: 'pigmentation_uniformity', label: 'Pigmentation Uniformity', hint: 'Higher = more uniform' },
  { key: 'flaking_level', label: 'Flaking Level', hint: 'Higher = more visible flaking' },
  { key: 'micro_line_visibility', label: 'Micro-Line Visibility', hint: 'Higher = more visible lines' },
];

function regionLabel(id) {
  if (id === 'forehead') return 'Forehead';
  if (id === 'cheek') return 'Cheek';
  if (id === 'left_cheek') return 'Left cheek';
  if (id === 'right_cheek') return 'Right cheek';
  return String(id || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function featureLabel(feature) {
  return String(feature || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function scoreTone(key, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const higherIsConcern = [
    'oiliness_appearance',
    'redness_level',
    'flaking_level',
    'micro_line_visibility',
  ].includes(key);
  if (higherIsConcern) {
    if (n >= 70) return 'is-high';
    if (n >= 45) return 'is-moderate';
    return 'is-good';
  }
  if (n >= 70) return 'is-good';
  if (n >= 45) return 'is-moderate';
  return 'is-high';
}

function RegionAnalysisBody({ region }) {
  const scores = region.scores || region.macro_analysis?.scores || {};
  const quality = region.image_quality || region.macro_analysis?.image_quality || null;
  const observations = Array.isArray(region.observations)
    ? region.observations
    : (region.macro_analysis?.observations || []);
  const mask = region.engagement_mask || region.macro_analysis?.engagement_mask || null;
  const insight =
    region.insight
    || region.macro_analysis?.insight
    || region.visual_summary
    || '';
  const limitations = Array.isArray(region.limitations)
    ? region.limitations
    : (region.macro_analysis?.limitations || []);
  const scoreRows = SCORE_META.filter((m) => scores[m.key] != null);

  return (
    <>
      <div className="skin-macro-report__trio">
        <figure className="skin-macro-report__figure">
          {region.original ? (
            <img src={region.original} alt={`${regionLabel(region.id)} original macro`} />
          ) : (
            <div className="skin-macro-report__empty">No original</div>
          )}
          <figcaption>Original · {regionLabel(region.id)}</figcaption>
        </figure>

        <figure className="skin-macro-report__figure">
          {mask?.image ? (
            <img src={mask.image} alt={`${regionLabel(region.id)} engagement mask`} />
          ) : region.overlay ? (
            <img src={region.overlay} alt={`${regionLabel(region.id)} attention overlay`} />
          ) : (
            <div className="skin-macro-report__empty">No engagement mask</div>
          )}
          <figcaption>
            Engagement mask
            {mask?.high_attention_regions != null
              ? ` · ${mask.high_attention_regions} focus area${mask.high_attention_regions === 1 ? '' : 's'}`
              : ''}
          </figcaption>
        </figure>

        <div className="skin-macro-report__panel">
          <h3>Macro observations</h3>
          {observations.length > 0 ? (
            <ul className="skin-macro-report__obs">
              {observations.slice(0, 5).map((o) => (
                <li key={`${o.feature}-${o.level}`}>
                  <span className="skin-macro-report__obs-name">{featureLabel(o.feature)}</span>
                  <span className={`skin-macro-report__obs-level is-${o.level}`}>{o.level}</span>
                  {o.confidence != null ? (
                    <span className="skin-macro-report__obs-conf">
                      {Math.round(Number(o.confidence) * 100)}%
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="skin-macro-report__muted">No strong macro observations for this frame.</p>
          )}

          {scoreRows.length > 0 ? (
            <div className="skin-macro-report__scores">
              <h4>0–100 visual scores</h4>
              <ul>
                {scoreRows.map((row) => {
                  const value = scores[row.key];
                  return (
                    <li key={row.key} title={row.hint}>
                      <div className="skin-macro-report__score-head">
                        <span>{row.label}</span>
                        <strong className={scoreTone(row.key, value)}>{value}</strong>
                      </div>
                      <div className="skin-macro-report__bar" aria-hidden>
                        <i
                          className={scoreTone(row.key, value)}
                          style={{ width: `${Math.max(4, Math.min(100, Number(value) || 0))}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {quality ? (
            <div className="skin-macro-report__quality">
              <h4>Capture quality</h4>
              <p>
                Focus {Math.round((quality.focus || 0) * 100)}%
                {' · '}
                Glare {Math.round((quality.glare || 0) * 100)}%
                {' · '}
                {quality.analysis_ready ? 'Ready for scoring' : 'Flagged — treat scores as directional'}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {insight ? (
        <div className="skin-macro-report__insight">
          <h3>Macro insight</h3>
          <p>{insight}</p>
        </div>
      ) : null}

      {limitations.length > 0 ? (
        <div className="skin-macro-report__limits">
          <h3>Limitations</h3>
          <ul>
            {limitations.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/**
 * Macro Skin Analysis (Microscope) — scores, engagement mask, observations.
 * pdfMode expands every region so original + mask are fully visible.
 */
export default function SkinMacroAnalysisReport({ macro = null, pdfMode = false }) {
  const regions = useMemo(() => {
    const map = macro?.regions && typeof macro.regions === 'object' ? macro.regions : {};
    const keys = [
      ...REGION_ORDER.filter((k) => map[k]),
      ...Object.keys(map).filter((k) => !REGION_ORDER.includes(k)),
    ];
    return keys
      .map((id) => ({ id, ...map[id] }))
      .filter((r) => r.scores || r.macro_analysis?.scores || r.original || r.engagement_mask?.image);
  }, [macro]);

  const [activeId, setActiveId] = useState(() => regions[0]?.id || 'forehead');
  const active = regions.find((r) => r.id === activeId) || regions[0] || null;

  if (!active) return null;

  const sessionScore = macro?.summary?.macro_condition_score;

  return (
    <div className={`skin-macro-report${pdfMode ? ' skin-macro-report--pdf' : ''}`}>
      {sessionScore != null ? (
        <p className="skin-macro-report__session">
          Session macro condition (avg): <strong>{sessionScore}/100</strong>
          <span> · Visual appearance scores — not lab measurements</span>
        </p>
      ) : (
        <p className="skin-macro-report__session">
          Visual appearance scores from USB microscope close-ups — not laboratory measurements.
        </p>
      )}

      {pdfMode ? (
        regions.map((region) => (
          <div key={region.id} className="skin-macro-report__pdf-region">
            <h3 className="skin-macro-report__pdf-heading">{regionLabel(region.id)}</h3>
            <RegionAnalysisBody region={region} />
          </div>
        ))
      ) : (
        <>
          <div className="skin-macro-report__tabs" role="tablist" aria-label="Macro analysis regions">
            {regions.map((r) => (
              <button
                key={r.id}
                type="button"
                role="tab"
                aria-selected={active.id === r.id}
                className={`skin-macro-report__tab ${active.id === r.id ? 'is-active' : ''}`}
                onClick={() => setActiveId(r.id)}
              >
                {regionLabel(r.id)}
                {r.scores?.macro_condition_score != null ? (
                  <span className="skin-macro-report__tab-score">{r.scores.macro_condition_score}</span>
                ) : null}
              </button>
            ))}
          </div>
          <RegionAnalysisBody region={active} />
        </>
      )}
    </div>
  );
}
